import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { useActingAthlete } from '../context/ActingAthleteContext';
import ConfirmModal from '../components/ConfirmModal';
import DeleteAthleteModal from '../components/DeleteAthleteModal';
import {
  EditorHero,
  MonoLabel,
  SectionCap,
  EditorialButton,
  EmptyState,
} from '../components/Editorial';

/**
 * Guardian "My Athletes" management page — web parity for the iOS guardian
 * athlete picker. Four actions per linked athlete:
 *   1. Act as athlete  — enterAthlete() sets the acting context (which injects
 *      ?as_athlete_id= into every /api call) then routes to the dashboard,
 *      where the athlete-shaped UI renders.
 *   2. Remove           — DELETE /guardians/{relationship_id} relinquishes the
 *      guardian relationship (confirmed via ConfirmModal).
 *   3. Delete athlete   — DELETE /guardians/athletes/{athlete_user_id}, two
 *      phase (the shared DeleteAthleteModal handles the requires_confirmation /
 *      Apple-subscription warning round-trip).
 *
 * Data source: GET /guardians/my-athletes. Auth is the global axios default
 * Authorization header set by AuthContext (no per-call config needed); these
 * /guardians/* calls intentionally operate on the guardian's own identity, so
 * the acting-athlete interceptor leaves them alone.
 */
export default function GuardianManagePage() {
  const { user, API_URL } = useAuth();
  const { enterAthlete } = useActingAthlete();
  const navigate = useNavigate();

  const [athletes, setAthletes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState('');
  const [messageType, setMessageType] = useState('');
  const [confirmAction, setConfirmAction] = useState(null);
  const [athleteToDelete, setAthleteToDelete] = useState(null);

  const flash = (text, type = 'success') => {
    setMessage(text);
    setMessageType(type);
    setTimeout(() => setMessage(''), 3500);
  };

  const fetchAthletes = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API_URL}/guardians/my-athletes`);
      setAthletes(data || []);
    } catch (err) {
      console.error('Failed to load managed athletes', err);
      flash('Could not load your athletes. Please try again.', 'error');
      setAthletes([]);
    } finally {
      setLoading(false);
    }
  }, [API_URL]);

  useEffect(() => {
    fetchAthletes();
  }, [fetchAthletes]);

  const fullName = (a) =>
    [a.athlete_first_name, a.athlete_last_name].filter(Boolean).join(' ') ||
    a.athlete_email ||
    `Athlete ${a.athlete_user_id}`;

  // Act as athlete: set the acting context, then route to the athlete home.
  const handleActAs = (a) => {
    enterAthlete(a);
    navigate('/dashboard');
  };

  // Remove (relinquish) the guardian relationship.
  const handleRemove = (a) => {
    setConfirmAction({
      title: `Remove ${fullName(a)}?`,
      message:
        'You will no longer be linked to this athlete or able to manage their account. ' +
        'This does not delete the athlete’s account.',
      confirmLabel: 'Remove',
      danger: true,
      onConfirm: async () => {
        setConfirmAction(null);
        try {
          await axios.delete(`${API_URL}/guardians/${a.relationship_id}`);
          flash(`Removed ${fullName(a)}.`);
          fetchAthletes();
        } catch (err) {
          flash(
            err.response?.data?.detail || 'Could not remove the relationship.',
            'error',
          );
        }
      },
    });
  };

  return (
    <div className="ed-page">
      {message && (
        <div
          className={`settings-toast ${messageType === 'error' ? 'settings-toast--error' : ''}`}
          role="alert"
        >
          {message}
        </div>
      )}

      <EditorHero
        eyebrow={
          <>
            <MonoLabel onDark>GUARDIAN · MY ATHLETES</MonoLabel>
            <MonoLabel onDark>·</MonoLabel>
            <MonoLabel onDark>
              {user?.first_name?.toUpperCase()} {user?.last_name?.toUpperCase()}
            </MonoLabel>
          </>
        }
        headline="Your athletes,"
        accent="managed."
        sub="Switch into an athlete to manage their profile, remove a link you no longer need, or delete an athlete account you control."
        stats={[
          { label: 'ATHLETES', value: athletes.length > 0 ? athletes.length : null },
        ]}
      />

      <SectionCap label="Linked athletes" />

      {loading && (
        <div className="ed-card">
          <div className="ed-card__title">Loading…</div>
        </div>
      )}

      {!loading &&
        athletes.map((a) => {
          const canEdit = a.permission === 'edit';
          const meta = [
            canEdit ? 'Edit access' : 'View only',
            a.relation || null,
            a.slot || null,
          ]
            .filter(Boolean)
            .join(' · ');
          return (
            <div className="ed-card" key={a.relationship_id}>
              <div
                className="ed-row ed-row--between"
                style={{ marginBottom: 8, gap: 12, alignItems: 'flex-start' }}
              >
                <div style={{ minWidth: 0 }}>
                  <div className="ed-card__title" style={{ fontWeight: 600 }}>
                    {fullName(a)}
                  </div>
                  {a.athlete_email && (
                    <div
                      style={{
                        fontSize: 13,
                        color: 'var(--ed-color-muted, #64748b)',
                        marginTop: 2,
                        wordBreak: 'break-word',
                      }}
                    >
                      {a.athlete_email}
                    </div>
                  )}
                  {meta && <MonoLabel>{meta.toUpperCase()}</MonoLabel>}
                </div>
              </div>

              <div className="ed-row" style={{ flexWrap: 'wrap', gap: 8 }}>
                <EditorialButton variant="solid" onClick={() => handleActAs(a)}>
                  Act as athlete
                </EditorialButton>
                <EditorialButton variant="ghost" onClick={() => handleRemove(a)}>
                  Remove
                </EditorialButton>
                {canEdit && (
                  <EditorialButton
                    variant="ghost"
                    onClick={() => setAthleteToDelete(a)}
                    style={{ color: 'var(--ed-accent-red, #dc2626)' }}
                  >
                    Delete athlete account
                  </EditorialButton>
                )}
              </div>
            </div>
          );
        })}

      {!loading && athletes.length === 0 && (
        <EmptyState
          title="No linked athletes yet."
          body="Once an athlete invites you and you accept, they'll appear here for you to manage."
        />
      )}

      {confirmAction && (
        <ConfirmModal
          title={confirmAction.title}
          message={confirmAction.message}
          confirmLabel={confirmAction.confirmLabel}
          danger={confirmAction.danger}
          onConfirm={confirmAction.onConfirm}
          onCancel={() => setConfirmAction(null)}
        />
      )}

      {athleteToDelete && (
        <DeleteAthleteModal
          athlete={athleteToDelete}
          onClose={() => setAthleteToDelete(null)}
          onDeleted={() => {
            const name = fullName(athleteToDelete);
            setAthleteToDelete(null);
            flash(`Deleted ${name}.`);
            fetchAthletes();
          }}
        />
      )}
    </div>
  );
}
